"use client"

import React, { useState } from "react"
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query"
import { ApiClientError } from "@/lib/ApiClientError"
import { toast } from "sonner"

export default function ReactQueryProvider({ children }: { children: React.ReactNode }) {
   const [queryClient] = useState(
      () =>
         new QueryClient({
            queryCache: new QueryCache({
               onError: (error, query) => {
                  if (error instanceof ApiClientError) {
                     console.error(`❌ Query failed: ${query.queryKey.join(" > ")}`, {
                        queryKey: query.queryKey,
                        message: error.message,
                        status: error.status,
                        payload: error.payload,
                     })
                  }
               },
            }),

            mutationCache: new MutationCache({
               onError: (error, variables, context, mutation) => {
                  if (mutation.options.meta?.suppressGlobalToast) return

                  if (error instanceof ApiClientError) {
                     const name = mutation.options.mutationKey?.join(" > ") ?? "Unknown mutation"

                     console.error(`❌ Mutation failed: ${name}`, {
                        mutationKey: mutation.options.mutationKey,
                        variables,
                        message: error.message,
                        status: error.status,
                        payload: error.payload,
                     })
                     toast.error(error.message || "Something went wrong")
                  }
               },
               onSuccess: (data, variables, context, mutation) => {
                  if (mutation.options.meta?.suppressGlobalToast) return

                  const customMessage = mutation.options.meta?.successMessage
                  if (typeof customMessage === "string") {
                     toast.success(customMessage)
                     return
                  }

                  const name = mutation.options.mutationKey?.join(" > ") ?? "Unknown mutation"
                  console.log(`✅ Mutation succeeded: ${name}`, { variables, data })
                  toast.success(`${name} succeeded`)
               },
            }),

            defaultOptions: {
               queries: {
                  staleTime: 5 * 60 * 1000,
               },
            },
         }),
   )
   return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
